/* eslint-disable max-classes-per-file */
import React from 'react';
import bind from 'bind-decorator';
import createFileReaderStream from 'filereader-stream';
import ImageDimensionsStream from 'image-dimensions-stream';
import FormData from 'form-data/lib/form_data';

import '@/lib/event-target';
import { Honeybadger } from '@/api/app/honeybadger';

export type UploadStartListener = (event: CustomEvent<{ files: FileList }>) => void;
export type UploadProgressListener =
  (event: CustomEvent<{ file: File, uploadedRatio: number }>) => void;
export type UploadCompleteListener<P> = (event: CustomEvent<P>) => void;
export type UploadErrorListener =
  (event: CustomEvent<{ files: FileList, error: FileUploaderError }>) => void;

export class FileUploaderError extends Error {
  userMessage: string;

  context: Record<string, any>;

  constructor(opts: { message: string, userMessage?: string, context?: Record<string, any> }) {
    super(opts.message);
    this.userMessage = opts.userMessage ?? opts.message;
    this.context = opts.context;
  }
}

export abstract class FileUploader<P> {
  private events = new EventTarget();

  constructor(public inputRef = React.createRef<HTMLInputElement>()) {}

  listenForInput() {
    const { current: input } = this.inputRef;
    const { onchange } = input;
    if (!onchange) {
      input.onchange = this.onInputChange;
    } else if (onchange !== this.onInputChange && !(onchange as any).fileuploader) {
      input.onchange = (...args) => {
        onchange.apply(input, args);
        this.onInputChange(...args);
      };
      Object.assign(input.onchange, { fileuploader: true });
    }
  }

  triggerUpload(files?: File[]) {
    if (!this.inputRef.current) {
      console.warn('FileUploader: cannot start an upload, input field was not rendered');
      return;
    }

    if (!files) {
      this.listenForInput();
      this.inputRef.current.click();
    } else if (files.length > 0) {
      this.reportUploadStarted(files);
      this.uploadFiles(files)
        .then((payload) => this.reportUploadCompleted(payload))
        .catch((e) => this.reportUploadError(files, e))
        .finally(() => {
          if (!this.inputRef.current) return;
          this.inputRef.current.value = '';
        });
    }
  }

  addUploadStartListener(listener: UploadStartListener) {
    this.events.addEventListener('upload-start', listener);
  }

  removeUploadStartListener(listener: UploadStartListener) {
    this.events.removeEventListener('upload-start', listener);
  }

  addUploadProgressListener(listener: UploadProgressListener) {
    this.events.addEventListener('upload-progress', listener);
  }

  removeUploadProgressListener(listener: UploadProgressListener) {
    this.events.removeEventListener('upload-progress', listener);
  }

  addUploadCompleteListener(listener: UploadCompleteListener<P>) {
    this.events.addEventListener('upload-complete', listener);
  }

  removeUploadCompleteListener(listener: UploadCompleteListener<P>) {
    this.events.removeEventListener('upload-complete', listener);
  }

  addUploadErrorListener(listener: UploadErrorListener) {
    this.events.addEventListener('upload-error', listener);
  }

  removeUploadErrorListener(listener: UploadErrorListener) {
    this.events.removeEventListener('upload-error', listener);
  }

  @bind protected onInputChange(event: Event) {
    const { files } = event.currentTarget as HTMLInputElement;
    this.triggerUpload(Array.from(files));
  }

  protected reportUploadStarted(files: File[]) {
    const event = new CustomEvent('upload-start', { detail: { files } });
    this.events.dispatchEvent(event);
  }

  protected reportUploadCompleted(payload: P) {
    const event = new CustomEvent('upload-complete', { detail: payload });
    this.events.dispatchEvent(event);
  }

  protected reportUploadError(files: File[], error: FileUploaderError) {
    error.userMessage ??= 'Error'; // eslint-disable-line no-param-reassign
    const event = new CustomEvent('upload-error', { detail: { files, error } });
    this.events.dispatchEvent(event);
  }

  protected reportUploadProgress(file: File, uploadedRatio: number) {
    const event = new CustomEvent('upload-progress', { detail: { file, uploadedRatio } });
    this.events.dispatchEvent(event);
  }

  protected abstract uploadFiles(files: File[]): Promise<P>;
}

export type S3UploadResults =
  Array<{ file: File, url: string, type: string, dimensions?: Record<string, number> }>;

export class S3FileUploader extends FileUploader<S3UploadResults> {
  constructor(
    protected s3Url: string,
    protected s3formData: Record<string, string>,
    protected defaultFileType: string,
    public inputRef: FileUploader<S3FileUploader>['inputRef'] = undefined,
  ) { super(inputRef); }

  async uploadFiles(files: File[]) {
    const result = files.map((file) => ({ file }) as S3UploadResults[number]);
    const promiseChain = result.reduce(
      (chain, payload) => chain.then(() => new Promise<void>(async (resolve, reject) => {
        // For some reason sometimes we get a file object that does not have Blob methods - https://app.honeybadger.io/projects/80430/faults/81014597/01FDWVAB1HS4CRD116EBNWFCWB
        if (!payload.file.slice) {
          Honeybadger.notify(new Error('Argument is not an instance of File'), {
            context: { files, constructors: files.map(((file) => file.constructor?.name)) },
          });
          return;
        }

        // Use own default file type to avoid the system default binary/octet-stream
        let { type: fileType = this.defaultFileType } = payload.file;
        // Override how chrome handles vcf files as text/dictionary
        if (payload.file.name?.endsWith('.vcf')) fileType = 'text/vcard';
        // Check if file type is allowed
        const allowedFileTypes = this.s3formData['File-Types'];
        if (allowedFileTypes && !allowedFileTypes.includes(fileType)) {
          reject(new FileUploaderError({
            message: `Invalid File Type! Please use one of the following: ${allowedFileTypes}`,
            context: { 'File Type': fileType, 'Allowed File Types': allowedFileTypes },
          }));
          return;
        }
        payload.type = fileType; // eslint-disable-line no-param-reassign

        let isFilePreloaded: boolean;
        const requiredHeadersJson = this.s3formData['Required-Headers'];
        const requiredHeaders = requiredHeadersJson && JSON.parse(requiredHeadersJson) as string[];
        if (requiredHeaders && requiredHeaders.length > 0 && fileType.includes('csv')) {
          isFilePreloaded = true;
          const err = await new Promise((innerResolve, innerReject) => {
            const fileReader = new FileReader();
            fileReader.onload = () => {
              const [firstLine] = fileReader.result.toString().split('\n');
              const missingHeaders = requiredHeaders
                .reduce((acc, header) => {
                  if (!new RegExp(header, 'i').test(firstLine)) acc.push(header);
                  return acc;
                }, []);

              if (missingHeaders.length < 1) innerResolve(null);
              else {
                const missingHeadersString = missingHeaders.join(', ');
                innerReject(new FileUploaderError({
                  message: `CSV header is missing the following column(s): ${missingHeadersString}`,
                  context: { 'Missing Headers': missingHeadersString },
                }));
              }
            };
            fileReader.readAsText(payload.file);
          }).catch((e) => e);
          if (err) {
            reject(err);
            return;
          }
        }

        let fileStream = !isFilePreloaded && createFileReaderStream(payload.file);
        const isImage = fileType.substr(0, 5) === 'image';
        if (isImage) {
          const sizeStream = new ImageDimensionsStream();
          sizeStream.on('dimensions', (dimensions) => {
            Object.assign(payload, { dimensions });
          });
          fileStream = fileStream.pipe(sizeStream);
        }

        const formData = new FormData();
        const { key: keyTemplate, ...s3Fields } = this.s3formData;
        const timestamp = Date.now().toString();
        const extension = payload.file.name?.split('.').pop();
        // eslint-disable-next-line no-template-curly-in-string
        const key = keyTemplate.replace('${filename}', extension ? `${timestamp}.${extension}` : timestamp);
        Object.entries(s3Fields).forEach(([k, v]) => formData.append(k, v));
        formData.append('key', key);
        formData.append('Content-Type', fileType);
        formData.append('file', fileStream ?? payload.file);

        let uploadSize = Number.MAX_SAFE_INTEGER;
        let uploaded = 0;
        formData.getLength((err, size) => {
          if (err) reject(err);
          uploadSize = size;
        });
        formData.on('data', (data) => {
          uploaded += data.length;
          this.reportUploadProgress(payload.file, uploaded / uploadSize);
        });

        formData.submit(this.s3Url, (err, res) => {
          if (err) {
            reject(err);
            return;
          }
          if (res.statusCode !== 201) {
            reject(new FileUploaderError({
              message: res.statusMessage,
              userMessage: 'Error',
              context: { response: res },
            }));
          }
          payload.url = this.s3Url + key; // eslint-disable-line no-param-reassign
          resolve();
        });
      })).catch((e) => {
        if (e instanceof FileUploaderError) throw e;
        throw new FileUploaderError({
          message: e.message,
          userMessage: 'Error',
          context: { 'File Type': payload.file.type },
        });
      }),
      Promise.resolve(),
    );
    await promiseChain;
    return result;
  }
}
